import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { useConversationContext } from "../context/ConversationContext";
import { formatPhoneNumber } from "../utils/handle-messages";
import { MessageCircle, User, Phone, Mail } from "lucide-react";

export function ConversationViewer({ queryType }: { queryType: "all" | "single" | null }) {
  const { conversationData, conversationsData } = useConversationContext();

  if (queryType === "single" && conversationData) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Informações do Contato
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Telefone:</span>
              <span>{formatPhoneNumber(conversationData.phone)}</span>
            </div>
            {conversationData.name && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Nome:</span>
                <span>{conversationData.name}</span>
              </div>
            )}
            {conversationData.cpf && (
              <div className="flex items-center gap-2">
                <span className="font-medium">CPF:</span>
                <span>{conversationData.cpf}</span>
              </div>
            )}
            {conversationData.email && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Email:</span>
                <span>{conversationData.email}</span>
              </div>
            )}
            {conversationData.userState && (
              <div className="flex items-center gap-2">
                <span className="font-medium">Estado:</span>
                <Badge variant="outline">{conversationData.userState}</Badge>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Mensagens ({conversationData.messages?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[500px] overflow-y-auto pr-4">
              <div className="space-y-4">
                {conversationData.messages?.map((message, index) => (
                  <div
                    key={message.id || index}
                    className={`flex ${
                      message.direction === "outgoing"
                        ? "justify-end"
                        : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-3 ${
                        message.direction === "outgoing"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">
                        {message.message}
                      </p>
                      <p className="text-xs mt-1 opacity-70">
                        {new Date(message.timestamp * 1000).toLocaleString(
                          "pt-BR"
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (queryType === "all" && conversationsData.length > 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Conversas Encontradas ({conversationsData.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[600px] overflow-y-auto">
            <div className="space-y-2">
              {conversationsData.map((conversation, index) => (
                <Card key={conversation.phone || index} className="cursor-pointer hover:bg-muted/50">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">
                            {formatPhoneNumber(conversation.phone)}
                          </span>
                          {conversation.userState && (
                            <Badge variant="outline" className="text-xs">
                              {conversation.userState}
                            </Badge>
                          )}
                        </div>
                        {conversation.name && (
                          <p className="text-sm text-muted-foreground">
                            {conversation.name}
                          </p>
                        )}
                        {conversation.lastMessage && (
                          <p className="text-sm text-muted-foreground mt-2 truncate">
                            {conversation.lastMessage.message}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <Badge variant="secondary">
                          {conversation.messages?.length || 0} msgs
                        </Badge>
                        {conversation.lastMessage && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(
                              conversation.lastMessage.timestamp * 1000
                            ).toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
}
